import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { FaTicketAlt, FaCopy, FaCheck, FaTag, FaClock, FaPercentage, FaRupeeSign, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import Footer from "./component/Footer";
import AccountSidebar from "./component/AccountSidebar";
import api from './api';

const MyCoupons = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("active");
    const [isVisible, setIsVisible] = useState(false);
    const [mobileView, setMobileView] = useState("menu");
    const [copiedCode, setCopiedCode] = useState(null);
    const [toastMsg, setToastMsg] = useState(null);
    const [searchCode, setSearchCode] = useState("");
    const [coupons, setCoupons] = useState([]);
    const [applyLoading, setApplyLoading] = useState(false);
    const [applyResult, setApplyResult] = useState(null);

    const storedData = localStorage.getItem("userData");
    const token = storedData ? JSON.parse(storedData).token : null;

    const showToast = (msg, type = "success") => {
        setToastMsg({ msg, type });
        setTimeout(() => setToastMsg(null), 3000);
    };

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
        const fn = () => setIsVisible(window.pageYOffset > 300);
        window.addEventListener('scroll', fn);
        return () => window.removeEventListener('scroll', fn);
    }, []);

    // ✅ Single-click mobile fix
    useEffect(() => {
        if (sessionStorage.getItem("mob_nav_direct") === "true") {
            sessionStorage.removeItem("mob_nav_direct");
            setMobileView("content");
        }
    }, []);

    const getCouponsApi = async () => {
        try {
            setLoading(true);
            const res = await api.get("/user/promoCodes", { headers: { Authorization: `Bearer ${token}` } });
            setCoupons(res.data.data || []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) getCouponsApi();
    }, [token, activeTab]);

    const handleCopy = (code) => {
        navigator.clipboard.writeText(code).then(() => {
            setCopiedCode(code);
            showToast(`Code "${code}" copied!`);
            setTimeout(() => setCopiedCode(null), 2500);
        });
    };

    const handleApplyCoupon = async () => {
        if (!searchCode.trim()) return;
        try {
            setApplyLoading(true);
            setApplyResult(null);
            const res = await api.post("/user/coupons/validate", { code: searchCode.trim().toUpperCase() }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setApplyResult({ success: true, message: res.data.message || "Coupon is valid!" });
        } catch (e) {
            setApplyResult({ success: false, message: e?.response?.data?.message || "Invalid or expired coupon code." });
        } finally {
            setApplyLoading(false);
        }
    };

    const getDiscountLabel = c => c.discountType === "percentage" ? `${c.discountValue}% OFF` : `₹${c.discountValue} OFF`;
    const getDiscountColor = c => c.discountType === "percentage" ? { bg: "#e8f4fd", color: "#1565c0" } : { bg: "#e6f9f0", color: "#1a7a4a" };
    const formatDate = d => !d ? "N/A" : new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    const getDaysLeft = d => !d ? null : Math.ceil((new Date(d) - new Date()) / (1000 * 60 * 60 * 24));

    const CouponCard = ({ c }) => {
        const ds = getDiscountColor(c);
        const dl = getDaysLeft(c.expiryDate);
        const exp = activeTab === "expired";

        return (
            <div className={`coupon-card ${exp ? 'coupon-expired' : ''}`}>
                <div className="coupon-strip" style={{ background: exp ? "#ccc" : "#c9a227" }} />
                <div className="coupon-left">
                    <div className="coupon-discount-circle" style={{ background: exp ? "#f0f0f0" : ds.bg, color: exp ? "#aaa" : ds.color }}>
                        {c.discountType === "percentage" ? <FaPercentage size={18} /> : <FaRupeeSign size={18} />}
                    </div>
                    <p className="coupon-discount-label" style={{ color: exp ? "#aaa" : ds.color }}>{getDiscountLabel(c)}</p>
                </div>
                <div className="coupon-divider" />
                <div className="coupon-body">
                    <div className="coupon-top-row">
                        <div>
                            <p className="coupon-name">{c.title || c.code}</p>
                            {c.description && <p className="coupon-desc">{c.description}</p>}
                        </div>
                        <span className={`coupon-status-badge ${exp ? 'expired' : 'active'}`}>{exp ? 'Expired' : 'Active'}</span>
                    </div>
                    <div className="coupon-meta-row">
                        {c.minOrderAmount > 0 && <span className="coupon-meta-chip"><FaTag size={10} /> Min ₹{c.minOrderAmount}</span>}
                        {c.maxDiscount > 0 && c.discountType === "percentage" && <span className="coupon-meta-chip"><FaTag size={10} /> Max ₹{c.maxDiscount}</span>}
                        {c.usageLimit > 0 && <span className="coupon-meta-chip"><FaCheck size={10} /> {c.usedCount || 0}/{c.usageLimit} used</span>}
                    </div>
                    <div className="coupon-footer-row">
                        <div className="coupon-expiry">
                            <FaClock size={11} />
                            {exp ? `Expired: ${formatDate(c.expiryDate)}` :
                                dl !== null ? dl <= 3 ? <span style={{ color: '#e53935' }}>Expires in {dl} day{dl !== 1 ? 's' : ''}!</span>
                                    : `Valid till ${formatDate(c.expiryDate)}`
                                : `Valid till ${formatDate(c.expiryDate)}`}
                        </div>
                        <div className="coupon-code-wrap">
                            <span className="coupon-code">{c.code}</span>
                            {!exp && (
                                <button className="copy-btn" onClick={() => handleCopy(c.code)}>
                                    {copiedCode === c.code ? <><FaCheck size={11} /> Copied</> : <><FaCopy size={11} /> Copy</>}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const CouponContent = () => (
        <>
            <div className="apply-coupon-bar">
                <p className="apply-bar-label"><FaTicketAlt size={14} /> Have a coupon code?</p>
                <div className="apply-input-row">
                    <input
                        type="text"
                        className="apply-input"
                        placeholder="Enter coupon code"
                        value={searchCode}
                        onChange={e => { setSearchCode(e.target.value); setApplyResult(null); }}
                        onKeyDown={e => e.key === 'Enter' && handleApplyCoupon()}
                    />
                    <button className="apply-btn" onClick={handleApplyCoupon} disabled={applyLoading || !searchCode.trim()}>
                        {applyLoading ? <span className="spinner-border spinner-border-sm" role="status" /> : "Check Code"}
                    </button>
                </div>
                {applyResult && (
                    <div className={`apply-result ${applyResult.success ? 'success' : 'error'}`}>
                        {applyResult.success ? <FaCheckCircle size={14} /> : <FaTimesCircle size={14} />} {applyResult.message}
                    </div>
                )}
            </div>

            <div className="tab-bar">
                <button className={`tab-btn ${activeTab === 'active' ? 'active' : ''}`} onClick={() => setActiveTab("active")}>Active</button>
            </div>

            {loading ? (
                <div className="loader-wrap">
                    <div className="spinner-border text-primary" role="status" />
                    <p style={{ marginTop: '12px' }}>Loading coupons...</p>
                </div>
            ) : coupons.length > 0 ? (
                coupons.map(c => <CouponCard key={c._id} c={c} />)
            ) : (
                <div className="empty-state">
                    <div className="empty-state-icon">🎟️</div>
                    <p>No active coupons found.</p>
                </div>
            )}
        </>
    );

    return (
        <>
            <style>{`
                .dashboard-wrapper { display:flex; min-height:80vh; padding:20px; gap:20px; }
                .dashboard-content { flex:1; min-width:0; padding:10px; }

                .acct-mobile-only-wrapper { display: none; }

                .wl-mob-header {
                    display: flex; align-items: center; justify-content: space-between;
                    padding: 13px 16px; background: #fff; border-bottom: 1px solid #f0f0f0;
                    position: sticky; top: 0; z-index: 10;
                }
                .wl-mob-back-btn { width: 36px; height: 36px; border: none; background: none; font-size: 28px; color: #333; cursor: pointer; }
                .wl-mob-title { font-size: 16px; font-weight: 700; color: #111; margin: 0; }
                .wl-mob-body { padding: 16px; background: #fff; min-height: auto; }

                /* Apply Coupon Bar */
                .apply-coupon-bar { background:#fff; border-radius:10px; box-shadow:0 2px 10px rgba(0,0,0,0.07); border:1px solid #f0f0f0; padding:18px 20px; margin-bottom:20px; }
                .apply-bar-label { font-size:13px; font-weight:700; color:#333; margin:0 0 12px 0; display:flex; align-items:center; gap:7px; }
                .apply-input-row { display:flex; gap:10px; }
                .apply-input { flex:1; padding:10px 14px; border:1.5px solid #e0e0e0; border-radius:7px; font-size:14px; font-weight:600; letter-spacing:1.5px; text-transform:uppercase; background:#fafafa; outline:none; }
                .apply-input:focus { border-color:#c9a227; background:#fff; }
                .apply-btn { padding:10px 22px; background:#c9a227; color:#fff; border:none; border-radius:7px; font-size:14px; font-weight:600; cursor:pointer; white-space:nowrap; }
                .apply-btn:hover:not(:disabled) { background:#a8861e; }
                .apply-result { display:flex; align-items:center; gap:8px; margin-top:12px; padding:10px 14px; border-radius:7px; font-size:13px; font-weight:500; }
                .apply-result.success { background:#e6f9f0; color:#1a7a4a; }
                .apply-result.error { background:#fdecea; color:#c62828; }

                /* Tab Button */
                .tab-bar { display:flex; gap:10px; margin-bottom:20px; }
                .tab-btn { padding:9px 28px; border:none; border-radius:6px; font-size:14px; font-weight:500; cursor:pointer; background:#e0e0e0; color:#555; }
                .tab-btn.active { background:#c9a227; color:#fff; box-shadow:0 2px 8px rgba(201,162,39,0.3); }

                /* Coupon Card */
                .coupon-card { 
                    background:#fff; border-radius:10px; box-shadow:0 2px 10px rgba(0,0,0,0.07); 
                    border:1px solid #f0f0f0; margin-bottom:16px; display:flex; align-items:stretch; overflow:hidden;
                }
                .coupon-strip { width:8px; background:#c9a227; flex-shrink:0; }
                .coupon-left { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:20px 16px; min-width:100px; }
                .coupon-discount-circle { width:58px; height:58px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:18px; }
                .coupon-discount-label { font-size:13px; font-weight:800; margin-top:6px; text-align:center; }
                .coupon-divider { width:1px; background:repeating-linear-gradient(to bottom, #ddd 0, #ddd 5px, transparent 5px, transparent 10px); }
                .coupon-body { flex:1; padding:16px 18px; }
                .coupon-top-row { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:10px; }
                .coupon-name { font-size:15px; font-weight:700; margin:0; }
                .coupon-desc { font-size:13px; color:#666; margin:4px 0 0 0; line-height:1.4; }
                .coupon-status-badge { padding:3px 12px; border-radius:20px; font-size:11px; font-weight:700; text-transform:uppercase; white-space:nowrap; }
                .coupon-status-badge.active { background:#fff8e7; color:#c9a227; }
                .coupon-status-badge.expired { background:#f0f0f0; color:#999; }
                .coupon-meta-row { display:flex; flex-wrap:wrap; gap:6px; margin:10px 0; }
                .coupon-meta-chip { background:#f5f5f5; color:#555; font-size:12px; padding:2px 9px; border-radius:20px; display:flex; align-items:center; gap:4px; }
                .coupon-footer-row { display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:10px; padding-top:10px; border-top:1px solid #eee; }
                .coupon-expiry { display:flex; align-items:center; gap:5px; font-size:12.5px; color:#666; }
                .coupon-code-wrap { display:flex; align-items:center; gap:8px; }
                .coupon-code { font-family: 'Courier New', monospace; font-size:15px; font-weight:700; color:#c9a227; background:#fff8e7; padding:6px 14px; border-radius:6px; letter-spacing:1.2px; border:1.5px dashed #c9a227; }
                .copy-btn { display:flex; align-items:center; gap:5px; padding:6px 12px; background:transparent; color:#c9a227; border:1px solid #c9a227; border-radius:6px; font-size:12.5px; cursor:pointer; }
                .copy-btn:hover { background:#c9a227; color:#fff; }

                .empty-state { text-align:center; padding:60px 20px; color:#888; }
                .empty-state-icon { font-size:52px; margin-bottom:12px; }
                .loader-wrap { text-align:center; padding:40px 20px; color:#555; }

                /* ==================== MOBILE RESPONSIVE ==================== */
                @media (max-width: 768px) {
                    .acct-mobile-only-wrapper { display: block; }
                    .dashboard-wrapper { display: none !important; }

                    .apply-input-row { flex-direction: column; gap: 10px; }
                    .apply-btn { width: 100%; }

                    .coupon-card { flex-direction: column; margin-bottom: 14px; }
                    .coupon-strip { width: 100%; height: 6px; }
                    .coupon-left { 
                        flex-direction: row; 
                        padding: 14px 16px; 
                        min-width: auto; 
                        gap: 16px; 
                    }
                    .coupon-divider { display: none; }
                    .coupon-body { padding: 14px 16px; }
                    .coupon-footer-row { 
                        flex-direction: column; 
                        align-items: flex-start; 
                        gap: 12px; 
                    }
                    .coupon-code-wrap { 
                        width: 100%; 
                        justify-content: space-between; 
                    }
                }

                @media (min-width: 769px) {
                    .wl-mob-content-panel { display: none !important; }
                    .dashboard-wrapper { display: flex !important; }
                }
            `}</style>

            <main className="main__content_wrapper">

                {/* ── MOBILE ── */}
                <div className="acct-mobile-only-wrapper">
                    <AccountSidebar
                        mobileView={mobileView}
                        setMobileView={setMobileView}
                        activeMobilePath="/my-coupan"
                        contentViewName="content"
                    />
                </div>

                {/* Mobile Content Panel */}
                <div className="wl-mob-content-panel" style={{ display: mobileView === "content" ? "block" : "none" }}>
                    <div className="wl-mob-header">
                        <button className="wl-mob-back-btn" onClick={() => setMobileView("menu")}>‹</button>
                        <h2 className="wl-mob-title">My Coupons</h2>
                        <div style={{ width: 36 }} />
                    </div>
                    <div className="wl-mob-body">
                        <CouponContent />
                    </div>
                </div>

                {/* ── DESKTOP ── */}
                <div className="container-fluid">
                    <div className="dashboard-wrapper">
                        <AccountSidebar
                            mobileView={mobileView}
                            setMobileView={setMobileView}
                            activeMobilePath="/my-coupan"
                            contentViewName="content"
                        />
                        <div className="dashboard-content">
                            <CouponContent />
                        </div>
                    </div>
                </div>

            </main>

            <Footer />

            {toastMsg && <div className={`toast-notification ${toastMsg.type}`}>{toastMsg.msg}</div>}

            <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                style={{
                    position: 'fixed', bottom: '30px', right: '30px',
                    width: '46px', height: '46px', borderRadius: '50%',
                    backgroundColor: '#ceb256', color: '#fff', border: 'none',
                    zIndex: 1000, cursor: 'pointer',
                    display: isVisible ? 'flex' : 'none',
                    alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.2)'
                }}>
                ↑
            </button>
        </>
    );
};

export default MyCoupons;